import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useMemo, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types";

import { FirstScheduleFooter } from "@/components/onboarding/first-schedule-footer";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import {
  CardSelectDescription,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { LocationCardSelect } from "@/components/ui/select-input/location-card-select";
import { useRecordFirstScheduleEntry } from "@/hooks/use-first-schedule-history";
import { useGraphQL } from "@/hooks/use-graphql";
import { firstScheduleAtom } from "@/lib/atoms/first-schedule-atom";
import {
  FIRST_SCHEDULE_STEPS,
  getFirstScheduleProgress,
} from "@/lib/helpers/first-schedule-flow";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const FirstScheduleInitQuery = graphql(`
  query FirstScheduleInitQuery {
    unidades(where: { ativo: { _eq: true } }) {
      codUnidade
      nomeLimpo
      sigla
    }
  }
`);

const firstScheduleSearchSchema = z.object({
  codAgendas: z.array(z.coerce.number()).optional(),
  returnTo: z.literal("appointment-type").optional(),
});

const FirstScheduleUnitPage = (): JSX.Element => {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { flags } = useRouteContext({ from: "__root__" });
  const [form, setForm] = useAtom(firstScheduleAtom);
  useRecordFirstScheduleEntry("location", !search.codAgendas && !search.returnTo);

  const showsAppointmentTypesSteps = useMemo(
    () => flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"],
    [flags],
  );

  const { data } = useGraphQL(FirstScheduleInitQuery);

  const unitsByValue = useMemo(() => {
    const map = new Map<
      string,
      { codUnidade: number; nomeLimpo: string; sigla: string }
    >();
    for (const unit of data?.unidades ?? []) {
      const value = unit.nomeLimpo?.toLowerCase().replaceAll(/\s+/g, "-") ?? "";
      map.set(value, {
        codUnidade: unit.codUnidade,
        nomeLimpo: unit.nomeLimpo ?? "",
        sigla: unit.sigla ?? "",
      });
    }
    return map;
  }, [data]);

  const currentDefaultValue = useMemo(() => {
    if (!form.selectedLocation) return undefined;
    for (const [value, unit] of unitsByValue) {
      if (unit.codUnidade === form.selectedLocation.codUnidade) return value;
    }
    return undefined;
  }, [unitsByValue, form.selectedLocation]);

  const handleUnitChange = (value: string): void => {
    const unit = unitsByValue.get(value);
    if (!unit) return;

    setForm((prev) => ({
      ...prev,
      selectedLocation: {
        codUnidade: unit.codUnidade,
        nomeLimpo: unit.nomeLimpo,
        sigla: unit.sigla,
      },
      // Reset times if the user switches the unit so step 2 reinitializes for the new location.
      locationTimes:
        prev.selectedLocation?.codUnidade === unit.codUnidade ? prev.locationTimes : [],
    }));
  };

  const handleContinue = (): void => {
    if (!form.selectedLocation) return;

    const { codUnidade, nomeLimpo, sigla } = form.selectedLocation;

    // Pré-popula os horários padrão para o step 2, evitando useEffect na tela de agenda.
    if (
      form.locationTimes.length === 0 ||
      form.locationTimes[0]?.locationId !== codUnidade
    ) {
      const DEFAULT_DAYS = [1, 2, 3, 4, 5];
      setForm((prev) => ({
        ...prev,
        locationTimes: [
          {
            locationId: codUnidade,
            times: DEFAULT_DAYS.map((dayOfWeek) => ({
              dayOfWeek,
              startTime: "08:00",
              endTime: "18:00",
            })),
          },
        ],
      }));
    }

    void navigate({
      to: "/onboarding/first-schedule/schedule",
      search: {
        codUnidade,
        nomeLimpo,
        sigla,
        returnTo: search.returnTo,
        codAgendas: search.codAgendas,
      },
    });
  };

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar
          progress={getFirstScheduleProgress(
            FIRST_SCHEDULE_STEPS.location,
            showsAppointmentTypesSteps,
          )}
        />
      </FlowHeader>

      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <CardSelectRoot
          onChange={handleUnitChange}
          name="first-schedule-location"
          className="gap-4"
          defaultValue={currentDefaultValue}
        >
          <div>
            <CardSelectTitle>Escolha onde você vai atender</CardSelectTitle>
            <CardSelectDescription>
              As reservas de sala serão permitidas conforme sua disponibilidade de
              horário.
            </CardSelectDescription>
          </div>
          <LocationCardSelect allowVirtualSchedules={false} />
        </CardSelectRoot>

        <FirstScheduleFooter
          onSubmit={handleContinue}
          disabled={form.selectedLocation === null}
        />
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/first-schedule/")({
  validateSearch: firstScheduleSearchSchema,
  component: FirstScheduleUnitPage,
});
